import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Skeleton,
  Button,
  Card,
  CardContent,
  Avatar
} from '@mui/material';
import { 
  AcademicCapIcon, 
  TrophyIcon, 
  CalendarIcon,
  RocketLaunchIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { fetchCertificatesByStudentId, downloadCertificate } from '../../services/certificateService';
import CertificationCard from '../../components/student/CertificationCard';
import CertificatePreviewModal from '../../components/student/CertificatePreviewModal';

const MyCertifications = () => {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const studentId = localStorage.getItem('user_id') || JSON.parse(localStorage.getItem('user') || '{}')?.userId;

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        if (studentId) {
          const data = await fetchCertificatesByStudentId(studentId);
          setCertificates(data || []);
        }
      } catch (err) {
        console.error('Failed to load certificates:', err);
        setError('We encountered an issue while retrieving your certifications. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [studentId]);

  const handleView = (cert) => {
    setSelectedCert(cert);
    setIsPreviewOpen(true);
  };

  const handleDownload = (cert) => {
    downloadCertificate(cert.certificate_pdf_url);
  };

  if (loading) return <LoadingState />;

  return (
    <Box sx={{ pb: 8, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      {certificates.length > 0 ? (
        <>
          {/* Hero Section */}
          <Box sx={{ 
            background: 'linear-gradient(135deg, #843FEF 0%, #6366F1 100%)',
            pt: { xs: 6, md: 8 },
            pb: { xs: 12, md: 14 },
            px: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Background Decorations */}
            <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', filter: 'blur(80px)' }} />
            <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: 250, height: 250, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', filter: 'blur(40px)' }} />

            <Container maxWidth="lg">
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={7}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ p: 1, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '12px', backdropFilter: 'blur(8px)' }}>
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </Box>
                    <Typography variant="overline" sx={{ fontWeight: 800, letterSpacing: '0.1em', opacity: 0.9 }}>
                      Verified Achievements
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 900, mb: 2, fontSize: { xs: '2.25rem', md: '3rem' } }}>
                    🎓 Your Achievements
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 500, opacity: 0.9, lineHeight: 1.6, maxWidth: '600px', mb: 4 }}>
                    Congratulations on earning verified certifications through your learning journey. 
                    These credentials demonstrate your skills, commitment, and readiness for future opportunities. 
                    Continue learning to unlock even more achievements.
                  </Typography>
                </Grid>
              </Grid>
            </Container>
          </Box>

          {/* Stats Section */}
          <Container maxWidth="lg" sx={{ mt: -8, position: 'relative', zIndex: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Certifications Earned" 
                  value={certificates.length} 
                  icon={<TrophyIcon className="h-6 w-6 text-[#843FEF]" />} 
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Programs Completed" 
                  value={new Set(certificates.map(c => c.programName || c.courseName)).size} 
                  icon={<CheckCircleIcon className="h-6 w-6 text-[#10B981]" />} 
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Latest Certification Date" 
                  value={certificates[0]?.issueDate ? new Date(certificates[0].issueDate).toLocaleDateString() : 'N/A'} 
                  icon={<CalendarIcon className="h-6 w-6 text-[#F59E0B]" />} 
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard 
                  title="Next Certification Goal" 
                  value="Advanced AI" 
                  icon={<SparklesIcon className="h-6 w-6 text-[#3B82F6]" />} 
                />
              </Grid>
            </Grid>
          </Container>

          {/* Certifications Grid */}
          <Container maxWidth="lg" sx={{ mt: 8 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                  My Certifications
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  Manage and showcase your verified achievements.
                </Typography>
              </Box>
            </Box>

            <Grid container spacing={4}>
              {certificates.map((cert) => (
                <Grid item xs={12} sm={6} md={4} key={cert._id || cert.id}>
                  <CertificationCard 
                    certificate={cert} 
                    onView={handleView} 
                    onDownload={handleDownload}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </>
      ) : (
        <EmptyState navigate={navigate} />
      )}

      <CertificatePreviewModal 
        open={isPreviewOpen} 
        onClose={() => setIsPreviewOpen(false)} 
        certificate={selectedCert} 
      />
    </Box>
  );
};

const StatCard = ({ title, value, icon }) => (
  <Card sx={{ 
    borderRadius: '24px', 
    border: '1px solid #E2E8F0', 
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
    height: '100%'
  }}>
    <CardContent sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: '16px' }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 900, color: '#0F172A' }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const EmptyState = ({ navigate }) => (
  <Box>
    <Box sx={{ 
      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)',
      py: { xs: 10, md: 15 },
      px: 4,
      textAlign: 'center'
    }}>
      <Container maxWidth="md">
        <Box sx={{ 
          width: 120, 
          height: 120, 
          bgcolor: 'white', 
          borderRadius: '40px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          margin: '0 auto',
          mb: 4,
          boxShadow: '0 20px 40px rgba(132, 63, 239, 0.1)',
          position: 'relative'
        }}>
          <AcademicCapIcon className="h-16 w-16 text-[#843FEF]" />
          <Box sx={{ 
            position: 'absolute', 
            top: -10, 
            right: -10, 
            bgcolor: '#F59E0B', 
            borderRadius: '50%', 
            p: 1, 
            boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)' 
          }}>
            <SparklesIcon className="h-5 w-5 text-white" />
          </Box>
        </Box>

        <Typography variant="h3" sx={{ fontWeight: 900, color: '#0F172A', mb: 2, fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
          Your Certification Journey Starts Here
        </Typography>
        
        <Typography variant="h6" sx={{ color: '#64748B', fontWeight: 500, lineHeight: 1.7, mb: 5, maxWidth: '700px', mx: 'auto' }}>
          You're making meaningful progress toward earning your first professional certification. 
          Complete your assigned bootcamp programs, achieve the required learning milestones, 
          and demonstrate your skills to unlock verified credentials that showcase your accomplishments.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<RocketLaunchIcon className="h-5 w-5" />}
            onClick={() => navigate('/bootcamp/my-bootcamps')}
            sx={{ 
              bgcolor: '#843FEF', 
              borderRadius: '16px', 
              px: 4, 
              py: 2, 
              fontWeight: 800,
              textTransform: 'none',
              fontSize: '1rem',
              boxShadow: '0 10px 20px rgba(132, 63, 239, 0.2)',
              '&:hover': { bgcolor: '#7035CC', transform: 'translateY(-2px)' },
              transition: 'all 0.2s'
            }}
          >
            Explore My Bootcamps
          </Button>
          
          <Button 
            variant="outlined" 
            size="large"
            onClick={() => navigate('/dashboard')}
            sx={{ 
              borderRadius: '16px', 
              px: 4, 
              py: 2, 
              fontWeight: 800,
              textTransform: 'none',
              fontSize: '1rem',
              borderColor: '#E2E8F0',
              color: '#64748B',
              '&:hover': { bgcolor: 'white', borderColor: '#843FEF', color: '#843FEF' },
              transition: 'all 0.2s'
            }}
          >
            Continue Learning
          </Button>
        </Box>
      </Container>
    </Box>

    <Container maxWidth="md" sx={{ mt: -6, mb: 10 }}>
      <Card sx={{ borderRadius: '32px', border: '1px solid #E2E8F0', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: { xs: 4, md: 6 } }}>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#0F172A', mb: 4, textAlign: 'center' }}>
            Why Earn Certifications?
          </Typography>
          
          <Grid container spacing={3}>
            <BenefitItem text="Validate your skills and knowledge" />
            <BenefitItem text="Strengthen your resume and professional profile" />
            <BenefitItem text="Showcase achievements to recruiters and employers" />
            <BenefitItem text="Build confidence through measurable accomplishments" />
            <BenefitItem text="Demonstrate commitment to continuous learning" />
          </Grid>
        </CardContent>
      </Card>
    </Container>
  </Box>
);

const BenefitItem = ({ text }) => (
  <Grid item xs={12} sm={6}>
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.5, p: 0.5, bgcolor: '#ECFDF5', borderRadius: '50%' }}>
        <CheckCircleIcon className="h-5 w-5 text-[#10B981]" />
      </Box>
      <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>
        {text}
      </Typography>
    </Box>
  </Grid>
);

const LoadingState = () => (
  <Container maxWidth="lg" sx={{ py: 8 }}>
    <Skeleton variant="rectangular" height={300} sx={{ borderRadius: '32px', mb: 4 }} />
    <Grid container spacing={4}>
      {[1, 2, 3].map(i => (
        <Grid item xs={12} sm={6} md={4} key={i}>
          <Skeleton variant="rectangular" height={400} sx={{ borderRadius: '24px' }} />
        </Grid>
      ))}
    </Grid>
  </Container>
);

export default MyCertifications;
